import { Button, Divider, Flex, Skeleton, Spacer } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { validateWithZod } from "src/services/formValidations";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import * as BasicInformation from "./BasicInformation";
import * as Contacts from "./Contacts";
import * as Email from "./Email";

export const OverviewTab = () => {
  const basicInformation = BasicInformation.useForm();
  const contacts = Contacts.useForm();
  const email = Email.useForm();

  const initialValuesRD = React.useMemo(
    () =>
      RD.toTuple3(
        basicInformation.initialValues,
        contacts.initialValues,
        email.initialValues
      ).map(
        ([
          basicInformationInitialValues,
          contactsInitialValues,
          emailFooterInitialValues,
        ]): FormikType => {
          return {
            ...basicInformationInitialValues,
            ...contactsInitialValues,
            ...emailFooterInitialValues,
          };
        }
      ),
    [basicInformation, contacts, email]
  );

  const toast = useAvelaToast();

  return (
    <RemoteDataView
      remoteData={initialValuesRD}
      error={(error) => <GenericError error={error} />}
      loading={<Loading />}
    >
      {(initialValues) => {
        return (
          <NavigationBlockerProvider>
            <Formik<FormikType>
              initialValues={initialValues}
              onSubmit={async (values, formik) => {
                try {
                  await Promise.all([
                    basicInformation.submit(values),
                    contacts.submit(values),
                    email.submit(values),
                  ]);
                  toast({
                    title: "Organization updated",
                    isClosable: true,
                    status: "success",
                  });
                  formik.resetForm({ values });
                } catch (error) {
                  console.error(error);
                  toast.error({
                    title: "Error editing organization",
                    description:
                      "Please try again later or report the problem to our support team",
                  });
                }
              }}
              validate={validateWithZod(FormikSchema)}
            >
              {(formik) => {
                return (
                  <Flex
                    as={Form}
                    direction="column"
                    height="100%"
                    grow="1"
                    gap={6}
                  >
                    <BasicInformation.Form />
                    <Divider />
                    <Contacts.Form />
                    <Email.Form />
                    <Spacer />
                    <AdminFormButtons insideTabPanel>
                      <Spacer />
                      <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        isDisabled={
                          !formik.isValid ||
                          !formik.dirty ||
                          formik.isSubmitting
                        }
                      >
                        Update
                      </Button>
                      <NavigationBlocker hasChanges={formik.dirty} />
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          </NavigationBlockerProvider>
        );
      }}
    </RemoteDataView>
  );
};

// prettier-ignore
const FormikSchema = BasicInformation.FormikSchema
  .merge(Contacts.FormikSchema)
  .merge(Email.FormikSchema);

type FormikType = z.infer<typeof FormikSchema>;

const Loading = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Spacer height="1rem" />
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
    </Flex>
  );
};
